import React from 'react';

import { Tabs as RadixTabs, useThemeContext } from '@radix-ui/themes';
import clsx from 'clsx';

export type TabsSize = 'small' | 'medium' | 'large';
export type TabsColor =
  | 'default'
  | 'primary'
  | 'secondary'
  | 'success'
  | 'warning'
  | 'error';

export interface TabsRootProps {
  children: React.ReactNode;
  defaultValue?: string;
  value?: string;
  onValueChange?: (value: string) => void;
  className?: string;
}

export interface TabsListProps {
  children: React.ReactNode;
  size?: TabsSize;
  color?: TabsColor;
  className?: string;
  highContrast?: boolean;
  style?: React.CSSProperties;
}

export interface TabsTriggerProps {
  children: React.ReactNode;
  value: string;
  disabled?: boolean;
  className?: string;
}

export interface TabsContentProps {
  children: React.ReactNode;
  value: string;
  className?: string;
  style?: React.CSSProperties;
}

const sizeMapping: Record<TabsSize, '1' | '2'> = {
  small: '1',
  medium: '2',
  large: '2', // Radix Themes Tabs only has size 1 and 2
};

function resolveColor(
  color: TabsColor,
  accentColor?: string
): string | undefined {
  switch (color) {
    case 'primary':
      return accentColor || 'cyan';
    case 'secondary':
      return 'gray';
    case 'success':
      return 'jade';
    case 'warning':
      return 'amber';
    case 'error':
      return 'crimson';
    case 'default':
    default:
      return undefined; // Use default Radix color
  }
}

/**
 * TabsRoot — wrapper for Radix Themes Tabs.Root.
 * Contains all the parts of a tabs component.
 */
export const TabsRoot: React.FC<TabsRootProps> = ({
  children,
  defaultValue,
  value,
  onValueChange,
  className,
}) => {
  return (
    <RadixTabs.Root
      defaultValue={defaultValue}
      value={value}
      onValueChange={onValueChange}
      className={clsx('tabs__root', className)}
    >
      {children}
    </RadixTabs.Root>
  );
};

/**
 * TabsList — wrapper for Radix Themes Tabs.List.
 * Contains the triggers that are aligned along the edge of the active content.
 */
export const TabsList: React.FC<TabsListProps> = ({
  children,
  size = 'medium',
  color = 'default',
  highContrast = false,
  className,
  style,
  ...props
}) => {
  const theme = useThemeContext();
  const accentColor = theme?.accentColor || 'cyan';

  const mappedSize = sizeMapping[size];
  const resolvedColor = resolveColor(color, accentColor);

  return (
    <RadixTabs.List
      highContrast={highContrast}
      size={mappedSize}
      className={clsx(
        'tabs__list',
        `tabs__list--size-${size}`,
        `tabs__list--color-${color}`,
        className
      )}
      style={{
        ...(resolvedColor &&
          ({ '--accent-color': resolvedColor } as React.CSSProperties)),
        ...style,
      }}
      {...props}
    >
      {children}
    </RadixTabs.List>
  );
};

/**
 * TabsTrigger — wrapper for Radix Themes Tabs.Trigger.
 * The button that activates its associated content.
 */
export const TabsTrigger: React.FC<TabsTriggerProps> = ({
  children,
  value,
  disabled = false,
  className,
}) => (
  <RadixTabs.Trigger
    value={value}
    disabled={disabled}
    className={clsx(
      'tabs__trigger',
      disabled && 'tabs__trigger--disabled',
      className
    )}
  >
    {children}
  </RadixTabs.Trigger>
);

/**
 * TabsContent — wrapper for Radix Themes Tabs.Content.
 * Contains the content associated with each trigger.
 */
export const TabsContent: React.FC<TabsContentProps> = ({
  children,
  value,
  className,
  style,
}) => (
  <RadixTabs.Content
    value={value}
    className={clsx('tabs__content', className)}
    style={style}
  >
    {children}
  </RadixTabs.Content>
);

TabsRoot.displayName = 'TabsRoot';
TabsList.displayName = 'TabsList';
TabsTrigger.displayName = 'TabsTrigger';
TabsContent.displayName = 'TabsContent';
